import { Component } from "react";
import { AlertCircle } from "lucide-react";

/**
 * Global error boundary. Catches React render errors AND becomes the last line
 * of defense against unhandled promise rejections (registered via window listener).
 *
 * In dev mode CRA still shows its red overlay for unhandled errors — we suppress
 * it by ensuring all known axios calls have .catch() handlers. This boundary
 * remains for unforeseen render bugs.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
  }

  reset = () => this.setState({ hasError: false, error: null });

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="error-boundary">
        <div className="max-w-md text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-red-50 flex items-center justify-center mb-4">
            <AlertCircle className="w-6 h-6 text-[#DC143C]" />
          </div>
          <h2 className="font-display text-2xl font-semibold text-slate-900">Une erreur est survenue</h2>
          <p className="text-slate-600 mt-2 text-sm leading-relaxed">
            Quelque chose s'est mal passé lors du chargement de cette page. Vous pouvez réessayer ou revenir à l'accueil.
          </p>
          <div className="flex gap-2 justify-center mt-6">
            <button onClick={this.reset} className="px-5 py-2.5 rounded-full bg-[#0056D2] hover:bg-[#0047AD] text-white text-sm font-medium transition">
              Réessayer
            </button>
            <a href="/" className="px-5 py-2.5 rounded-full border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium transition">
              Accueil
            </a>
          </div>
        </div>
      </div>
    );
  }
}
